import { useEffect, useRef, useState } from "react";

const DEFAULT_MIN_DURATION_MS = 300;

/**
 * Keeps a loading indicator visible for a minimum duration without delaying loading start.
 *
 * @param {boolean} isLoading - Whether the underlying operation is currently loading.
 * @param {number} minDuration - Minimum visible loading duration in milliseconds.
 * @returns {boolean} Visual loading state.
 */
export const useMinLoadingDuration = (
  isLoading: boolean,
  minDuration: number = DEFAULT_MIN_DURATION_MS,
): boolean => {
  const [isVisuallyLoading, setIsVisuallyLoading] =
    useState<boolean>(isLoading);

  const loadingStartedAtRef = useRef<number | null>(
    isLoading ? Date.now() : null,
  );

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoading) {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      loadingStartedAtRef.current = Date.now();
      setIsVisuallyLoading(true);

      return undefined;
    }

    const elapsed =
      loadingStartedAtRef.current !== null
        ? Date.now() - loadingStartedAtRef.current
        : Infinity;

    if (elapsed >= minDuration) {
      setIsVisuallyLoading((prev) => (prev ? false : prev));
    } else {
      const remaining = minDuration - elapsed;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setIsVisuallyLoading(false);
      }, remaining);
    }

    return (): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isLoading, minDuration]);

  return isLoading || isVisuallyLoading;
};
